import { Trigger as RadixTrigger, Root } from "@radix-ui/react-dropdown-menu";
import CustomTooltip from "app/components/Tooltip";
import { selectProjectMemberList } from "app/containers/Features/components/MemberSelection/redux/selector";
import { actions as memberSelectionActions } from "app/containers/Features/components/MemberSelection/redux/slice";
import { selectTimeTrackerForm } from "app/containers/Features/containers/TimeSheet/redux/selectors";
import { timeSheetActions } from "app/containers/Features/containers/TimeSheet/redux/slice";
import { selectUserFormData } from "app/redux/selector";
import clsx from "clsx";
import React, { ComponentProps, FC, ReactNode, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { dateFormatter } from "utils/helpers";
import { Portal } from "./Portal";

export const TimeTracker: FC<{
  tooltipText: string;
  children: ReactNode;
  assigneeId: string;
  portalProps: ComponentProps<typeof Portal>;
  menuVisibility?: boolean;
  onOpenChange?: (open: boolean) => void;
  toolTipPosition?: "top" | "bottom" | "left" | "right";
  triggerClassName?: string;
  needToFetchMembers?: boolean;
}> = ({
  tooltipText,
  children,
  assigneeId,
  portalProps,
  menuVisibility,
  onOpenChange,
  toolTipPosition,
  triggerClassName,
  needToFetchMembers = false,
}) => {
  // Hooks
  const dispatch = useDispatch();

  // Global States
  const projectMembers = useSelector(selectProjectMemberList);
  const currentUserDetail = useSelector(selectUserFormData);
  const timeTrackerForm = useSelector(selectTimeTrackerForm);

  // Variables
  const userId = projectMembers.find(
    (member) => member._id === assigneeId,
  )?.userId;
  const isAssignee = assigneeId ? currentUserDetail._id === userId : true;
  const projectId = portalProps?.selectedTicketDetails?.projectId;

  useEffect(() => {
    if (menuVisibility) {
      dispatch(
        timeSheetActions.setTimeTrackerForm({
          ...timeTrackerForm,
          date: dateFormatter(new Date(), "onlyDate"),
          startTime: new Date().toISOString(),
          endTime: new Date().toISOString(),
          hours: 0,
          minutes: 0,
        }),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, menuVisibility]);

  useEffect(() => {
    if (!menuVisibility || !projectId || !needToFetchMembers) return;

    dispatch(
      memberSelectionActions.fetchProjectMembersList({
        id: projectId,
        callback() {},
      }),
    );
  }, [dispatch, menuVisibility, projectId, needToFetchMembers]);

  return (
    <Root open={menuVisibility} onOpenChange={onOpenChange}>
      <RadixTrigger
        className={clsx(
          "flex items-center justify-center rounded-sm outline-none select-none ring-0 hover:bg-gray-200 border border-white",
          triggerClassName,
          menuVisibility && "border-secondary",
        )}
      >
        {tooltipText ? (
          <CustomTooltip
            text={tooltipText}
            align="center"
            position={toolTipPosition || "bottom"}
          >
            {children}
          </CustomTooltip>
        ) : (
          children
        )}
      </RadixTrigger>

      <Portal
        {...portalProps}
        isAssignee={portalProps?.isAssignee || isAssignee}
        assigneeId={portalProps?.assigneeId || assigneeId}
      />
    </Root>
  );
};
